import { NgClass, UpperCasePipe } from "@angular/common";
import {
  Component,
  computed,
  inject,
  ChangeDetectionStrategy,
} from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { ActivatedRoute, Router, RouterLink } from "@angular/router";
import { IconComponent } from "@claas/claas-ui-components";
import { TranslatePipe } from "@ngx-translate/core";
import { map } from "rxjs";
import { AppSectionHeader } from "../../../shared/components/app-section-header/app-section-header";
import { ConfigService } from "../../../shared/config/config.service";
import {
  pathRouteDataHelper$,
  pathRouteParametersHelper$,
} from "../../../shared/helpers/path-params.helper";
import {
  DefaultArgument,
  DefaultProduct,
} from "../../../shared/pia-api/generated";
import { AuthImagePipe } from "../../../shared/pipes/auth-image.pipe";
import { StringReplacePipe } from "../../../shared/pipes/string-replace.pipe";
import { FavoriteArgumentsService } from "../../../shared/services/favorite-arguments.service";
import { ModalCommunicationService } from "../../../shared/services/modal-communication-service";

type ArgumentWithMedia = DefaultArgument & {
  // __metadata / media_src mirror the PIA OData API response shape and are
  // intentionally not camelCase.
  // eslint-disable-next-line @typescript-eslint/naming-convention
  __metadata?: { media_src?: string };
};

@Component({
  selector: "claas-product-argument-detail-dialog",
  imports: [
    AppSectionHeader,
    AuthImagePipe,
    IconComponent,
    NgClass,
    RouterLink,
    StringReplacePipe,
    TranslatePipe,
    UpperCasePipe,
  ],
  templateUrl: "./product-argument-detail-dialog.html",
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: "./product-argument-detail-dialog.scss",
})
export class ProductArgumentDetailDialog {
  config = inject(ConfigService);
  route = inject(ActivatedRoute);
  favoriteArgumentsService = inject(FavoriteArgumentsService);
  modalCommunicationService = inject(ModalCommunicationService);
  router = inject(Router);

  currentUrl = this.router.url;

  data = toSignal(pathRouteDataHelper$());
  params = toSignal(pathRouteParametersHelper$());
  favoriteProductArguments = toSignal(
    this.favoriteArgumentsService.includedDocumentIds$,
  );
  countryID = toSignal(
    pathRouteParametersHelper$().pipe(
      map((params) =>
        params["country"]?.toLowerCase()
          ? encodeURIComponent(params["country"]?.toLowerCase() as string)
          : "",
      ),
    ),
    {
      initialValue: "",
    },
  );

  languageID = toSignal(
    pathRouteParametersHelper$().pipe(
      map((params) =>
        params["language"]?.toLowerCase()
          ? encodeURIComponent(params["language"]?.toLowerCase() as string)
          : "",
      ),
    ),
    {
      initialValue: "",
    },
  );

  year = toSignal(
    pathRouteParametersHelper$().pipe(
      map((params) =>
        params["year"]?.toLowerCase()
          ? encodeURIComponent(params["year"]?.toLowerCase() as string)
          : "",
      ),
    ),
    {
      initialValue: "",
    },
  );

  productSeriesID = toSignal(
    pathRouteParametersHelper$().pipe(
      map((params) =>
        params["id"] ? encodeURIComponent(params["id"] as string) : "",
      ),
    ),
    {
      initialValue: "",
    },
  );

  productID = toSignal(
    pathRouteParametersHelper$().pipe(
      map((params) =>
        params["productId"]
          ? encodeURIComponent(params["productId"] as string)
          : "",
      ),
    ),
    {
      initialValue: "",
    },
  );

  competitorID = toSignal(
    pathRouteParametersHelper$().pipe(
      map((params) =>
        params["competitorId"]
          ? encodeURIComponent(params["competitorId"] as string)
          : "",
      ),
    ),
    {
      initialValue: "",
    },
  );

  productSeries = computed(
    () => this.data()?.["productDetails"] as DefaultProduct | null,
  );

  product = computed(() => {
    const productId = this.params()?.["productId"];
    return this.data()?.["productDescendants"]?.find(
      (productDescendant: DefaultProduct) => {
        return productDescendant.ID === productId;
      },
    ) as DefaultProduct | null;
  });
  productArgument = computed(() => {
    const isCompetitor = this.isCompetitor();
    const argumentId = this.params()?.["argumentId"];

    if (!isCompetitor) {
      // Check if we're in comparison context (argumentsComparison exists) or regular context (arguments exists)
      const argumentsComparison = this.data()?.["argumentsComparison"];
      const regularArguments = this.data()?.["arguments"];

      if (argumentsComparison && Array.isArray(argumentsComparison)) {
        // In comparison context, product arguments are at index 0
        return argumentsComparison[0]?.find(
          (argument: DefaultArgument) => argument.ID === argumentId,
        ) as ArgumentWithMedia | null;
      } else {
        // In regular context, use the arguments array
        return regularArguments?.find(
          (argument: DefaultArgument) => argument.ID === argumentId,
        ) as ArgumentWithMedia | null;
      }
    } else {
      // Competitor arguments are at index 1
      return this.data()?.["argumentsComparison"]?.[1]?.find(
        (argument: DefaultArgument) => argument.ID === argumentId,
      ) as ArgumentWithMedia | null;
    }
  });

  isFavourite = computed(
    () =>
      this.productArgument()?.ID &&
      this.favoriteProductArguments()?.includes(
        this.productArgument()?.ID as string,
      ),
  );

  routeType = toSignal(
    pathRouteDataHelper$().pipe(map((data) => data?.["type"] || "product")),
    {
      initialValue: "product",
    },
  );

  isCompetitor = computed(() => this.routeType() === "competitor");

  handleFavoriteClick(id?: string): void {
    if (!id) return;

    if (!this.favoriteProductArguments()?.includes(id.trim())) {
      this.favoriteArgumentsService.add(id.trim() as string);
    } else {
      this.favoriteArgumentsService.remove(id.trim() as string);
    }
  }

  handleCloseArgumentDetailDialog() {
    this.modalCommunicationService.closeModal();
  }
}
